<template>
  <div class="card">
    <img
      class="logo"
      src="/images/terrassa-logo.png"
      alt="Logo Terrassa"
      @click="goHome"
    />
    <div class="vetlla-form">
      <h2>
        Puja imatge o escriu un missatge per la vetlla de
        {{ vetlla?.NombreDifunto }}
      </h2>
      <ImageInputTest
        ref="imageInput"
        :idVetlla="idVetllaProp"
        @update:base64Foto="foto = $event"
      />
      <input
        v-model="missatge"
        class="form-input"
        type="text"
        placeholder="MISSATGE"
      />
      <input
        v-model="signatura"
        class="form-input"
        type="text"
        placeholder="SIGNATURA"
      />
      <button
        class="crea-button"
        @click="uploadContenidoVetlla"
        :class="{ 'crea-button--cargando': isEnviando }"
      >
        <template v-if="isEnviando">
          <font-awesome-icon
            icon="spinner"
            class="crea-button__enviando-icon"
          />
        </template>
        <template v-else>Envia</template>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from "vue";
import { useRouter, useRoute } from "vue-router";

import axios from "axios";

import ImageInputTest from "../components/ImageInputTest.vue";
import { Vetlla, Sala } from "../types/vetlla.ts";
import CustomSwal from "../custom-swal.ts";
import useErpApiClient from "../erp-api-client";
import { dateFromFechaString } from "../helpers";

export default defineComponent({
  components: {
    ImageInputTest,
  },
  props: {
    id: {
      required: true,
      type: Number,
    },
  },
  setup(props) {
    const erpClient = useErpApiClient();
    const router = useRouter();
    const route = useRoute();

    const idVetllaProp = ref<number>(-1);
    const vetlla = ref<null | Vetlla>(null);

    const imageInput = ref<InstanceType<typeof ImageInputTest> | null>(null);
    const missatge = ref("");
    const signatura = ref("");

    const foto = ref("");

    const isEnviando = ref(false);
    const uploadContenidoVetlla = async () => {
      if (isEnviando.value) return;

      if (missatge.value.length > 200) {
        CustomSwal.fire({
          icon: "warning",
          title: "Missatge massa llarg",
          text: "El missatge no pot tenir més de 200 caràcters.",
        });
        return;
      }

      if (
        missatge.value === "" &&
        signatura.value === "" &&
        foto.value === ""
      ) {
        CustomSwal.fire({
          icon: "warning",
          title:
            "S'ha d'emplenar com a mínim un camp per tal d'enviar el condol",
        });
        return;
      }

      isEnviando.value = true;

      let isVisible = route.query.c === "0";

      const imageInputComponent = imageInput.value;
      if (!imageInputComponent) {
        CustomSwal.fire({
          icon: "error",
          title: "Error intern",
          text: "No s'ha pogut accedir al component d'imatge.",
        });
        isEnviando.value = false;
        return;
      }
      try {
        if (foto.value !== "") {
          await imageInputComponent.uploadImage({
            missatge: missatge.value,
            signatura: signatura.value,
            isVisible: isVisible,
          });
        } else {
          await axios.post("/api/condol/upload", {
            idVetlla: idVetllaProp.value,
            missatge: missatge.value,
            signatura: signatura.value,
            isVisible: isVisible,
          });
        }

        CustomSwal.fire({
          icon: "success",
          title: "Èxit",
        }).then(() => {
          window.location.reload();
        });
      } catch (error) {
        CustomSwal.fire({
          icon: "error",
          title: "Error enviant el condol",
          text: "Revisa la connexió o els camps",
        });
        console.error("❌ Error:", error);
      }
      isEnviando.value = false;
    };

    onMounted(() => {
      const requestVetllaInfo = () => {
        return axios
          .get(`/api/vetlla/read?id=${idVetllaProp.value}`)
          .then((response) => {
            vetlla.value = response.data as Vetlla;
            return vetlla.value;
          })
          .catch((e) => {
            console.error("Error obtenint vetlla:", e);
            return null;
          });
        //   .catch((e) => {
        //     CustomSwal.fire({
        //       icon: "error",
        //       title: "S'ha produït un error",
        //       text: `No s'ha pogut trobar la vetlla amb id '${idVetllaProp.value}'`,
        //     });
        //     console.error(e);
        //     return null;
        //   });
      };

      const checkExpiracion = (vetlla: null | Vetlla) => {
        if (null !== vetlla) {
          const end = dateFromFechaString(vetlla.FechaFin);
          const now = new Date();

          if (now.getTime() > end.getTime()) {
            router.push({ name: "expired" });
          }
        }
      };

      if ("sala" === route.meta.uploadType) {
        let salas: Sala[] = [];
        erpClient
          .request(() => axios.get("/api/sala/index"))
          .then((response) => {
            salas = response.data;
            const salaPresentation = salas.find((s) => props.id == s.Id);

            if (undefined === salaPresentation) {
              CustomSwal.fire({
                icon: "error",
                title: "S'ha produït un error",
                text: `No s'ha pogut trobar la sala amb id '${props.id}'`,
              });
            } else {
              idVetllaProp.value = salaPresentation.IdVetlla;
              requestVetllaInfo().then(checkExpiracion);
            }
          });
      } else {
        idVetllaProp.value = props.id;
        requestVetllaInfo().then(checkExpiracion);
      }
    });

    const goHome = () => {
      router.push({ name: "home" });
    };

    return {
      vetlla,
      missatge,
      signatura,
      foto,
      isEnviando,
      uploadContenidoVetlla,
      goHome,
      idVetllaProp,
      imageInput,
    };
  },
});
</script>

<style lang="scss" scoped>
.card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2em;

  padding: 1em;
  border-radius: $rounded-border;

  background-color: $bg-color;

  width: 80%;
  @media (max-width: 768px) {
    width: 96%;
  }
}

.logo {
  width: 16em;
}

.vetlla-form {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  gap: 1em;

  width: 24em;
  @media (max-width: 26em) {
    width: 100%;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.crea-button {
  @include button($primary-color);

  &__enviando-icon {
    color: contrast-color($primary-color);
    animation: spin 2s linear infinite;
  }

  &--cargando {
    background-color: color.adjust($primary-color, $lightness: -10%);
  }
}
</style>
